import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { Rail } from "../../../../../http/border/types/rails";
import { RailCap } from "../../../../../http/border/types/railCaps";
import { useRailsApi } from "../../../../../http/border/useRails";
import { useRailCapsApi } from "../../../../../http/border/useRailCaps";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface RailCapFormBody {
  item: SearchAdminItem | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  railIds: string[];
}

interface RailCapFormProps {
  railCap?: RailCap;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose: () => void;
}

const RailCapForm: FC<RailCapFormProps> = ({ railCap, isDisabled, isLoading, onClose }) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);
  const aborter = useRef(new AbortController());

  const itemsApi = useItemsApi();
  const railsApi = useRailsApi();
  const railCapsApi = useRailCapsApi();
  const rails = railsApi.useAdminRails({ limit: 100 }, aborter.current.signal);

  const defaultValues = {
    item: railCap?.item ?? null,
    order: railCap?.order ?? 50,
    visibility: railCap?.visibility ?? "hidden",
    railIds: railCap?.rails.map((rail) => rail.item.id) ?? [],
  };

  return (
    <Form<RailCapFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        setIsSubmitting(true);

        if (railCap?.item.id) {
          railCapsApi
            .updateAdminRailCap(railCap.item.id, {
              order: formData.order!,
              visibility: formData.visibility,
              railIds: formData.railIds,
            })
            .then(() => onClose())
            .catch((err) => dispatch(showErrorNotification(i18n, err)))
            .finally(() => setIsSubmitting(false));
        } else {
          try {
            await railCapsApi.insertAdminRailCap({
              itemId: formData.item?.id ?? "",
              order: formData.order!,
              visibility: formData.visibility,
              railIds: formData.railIds,
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [railCap]);

        const railIds = watch("railIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                    {({ labelId }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                      // Fetch initial data on mount
                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultOptions);
                      }, []);

                      return (
                        <CustomAsyncSelect
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultOptions}
                          isDisabled={!!railCap || isDisabled}
                          loadOptions={loadOptions}
                          isClearable
                          getOptionLabel={itemLabel}
                          getOptionValue={(x) => x.id}
                          isInvalid={fieldState.invalid}
                          onChange={(value) => setValue("item", value ?? null)}
                        />
                      );
                    }}
                  </FormField>
                );
              }}
            />
            <FormField label={i18n.translation.rails.plural}>
              <MultiCheckbox
                disabled={isDisabled || isLoading || isSubmitting}
                values={
                  rails.data?.items.map((rail: Rail) => ({
                    id: rail.item.id,
                    title: itemLabel(rail.item),
                    selected: railIds.includes(rail.item.id),
                  })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue("railIds", checked ? [...railIds, id] : railIds.filter((railId) => railId !== id));
                }}
              />
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default RailCapForm;
